import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { SlotsDataModel } from '@/services/scheduling/use-retrieve-slots';

/**
 * POST /api/Appointment/RetrieveResourceSlots — PROD-2383.
 *
 * The DEVICE mirror of `useRetrieveSlots` (`POST /api/Appointment/RetrieveSlots`).
 * The provider body names a `providerId` and a PRACTITIONER appointment
 * profile; a device has neither, and before this route existed a device sent
 * through the provider shape silently probed `Guid.Empty` and answered every
 * occurrence "Not available". This body names the device and its product
 * instead; the server sizes each occurrence by the product's minutes and asks
 * the device's own calendar.
 *
 * The answer has the provider answer's field names (`allSlotsAvailable`,
 * `slotDetails[].slot / nearestSlots / appointmentTime`), so the recurrence
 * configurator's actor-blind conflict logic (`recurrence-conflict.ts`) reads
 * it unchanged. The one difference is the slot, which carries `deviceId`
 * where the provider's carries `providerId`.
 */
export interface RetrieveResourceSlotsParams {
  deviceId: string;
  /** The device's product (Practice.ResourceAppointmentProfiles id). */
  deviceProfileId: string;
  practiceId: string;
  /** Local wall clock, no `Z` — the first session. */
  startDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  /** Backend period enum — "Days" | "Weeks" | "Months". */
  period: string;
  /** Full weekday names; sent only for "Weeks", exactly as the provider body. */
  days?: string[];
}

/** A free device window, as the device route ships it. */
export interface ResourceSlot {
  start?: string;
  end?: string;
  deviceId?: string;
}

interface ResourceSlotsResponse {
  allSlotsAvailable?: boolean;
  slotDetails?: Array<{
    appointmentTime?: string;
    slot?: ResourceSlot | null;
    nearestSlots?: ResourceSlot[] | null;
  }>;
}

export function buildRetrieveResourceSlotsBody(params: RetrieveResourceSlotsParams) {
  return {
    deviceId: params.deviceId,
    deviceProfileId: params.deviceProfileId,
    practiceId: params.practiceId,
    startDate: params.startDate,
    repeatEvery: params.repeatEvery,
    numberOfRepeat: params.numberOfRepeat,
    period: params.period,
    days: params.period === 'Weeks' ? (params.days ?? []) : null,
  };
}

/**
 * Normalise the device answer into the configurator's `SlotsDataModel`. The
 * slot keeps only `start` / `end`: those are the fields the conflict logic and
 * the booking read, and a device session's end is the server's to derive anyway.
 * Nulls become `undefined` so `detail.slot != null` means exactly what it does
 * for the provider answer.
 */
export function toSlotsDataModel(res: ResourceSlotsResponse | null | undefined): SlotsDataModel {
  const toSlot = (s: ResourceSlot) => ({ start: s.start, end: s.end });
  return {
    allSlotsAvailable: !!res?.allSlotsAvailable,
    slotDetails: (res?.slotDetails ?? []).map((d) => ({
      appointmentTime: d.appointmentTime,
      slot: d.slot ? toSlot(d.slot) : undefined,
      nearestSlots: d.nearestSlots ? d.nearestSlots.map(toSlot) : undefined,
    })),
  };
}

export function useRetrieveResourceSlots() {
  return useMutation<SlotsDataModel, Error, RetrieveResourceSlotsParams>({
    mutationFn: async (params) =>
      toSlotsDataModel(
        await post<ResourceSlotsResponse>(
          '/api/Appointment/RetrieveResourceSlots',
          buildRetrieveResourceSlotsBody(params),
        ),
      ),
  });
}
